import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import storage from '../utils/storage';
import { getBackendUrl } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

const Login = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  
  // Get redirect URL from query params
  const redirectUrl = searchParams.get('redirect') || '/';
  
  // Get backend URL at runtime (not build time)
  const BACKEND_URL = useMemo(() => {
    const url = getBackendUrl();
    console.log('[Login] Using backend URL:', url);
    return url;
  }, []);
  
  const [step, setStep] = useState('login'); // 'login' or '2fa'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Load saved credentials on mount (with safe storage)
  React.useEffect(() => {
    const savedUsername = storage.getItem('rememberedUsername');
    const savedPassword = storage.getItem('rememberedPassword');
    if (savedUsername && savedPassword) {
      setUsername(savedUsername);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        username,
        password
      });

      // Check if 2FA is required
      if (response.data.requires_2fa) {
        // Save credentials if Remember Me is checked (with safe storage)
        if (rememberMe) {
          storage.setItem('rememberedUsername', username);
          storage.setItem('rememberedPassword', password);
        } else {
          storage.removeItem('rememberedUsername');
          storage.removeItem('rememberedPassword');
        }
        setStep('2fa');
      } else {
        // 2FA disabled - direct login successful
        // Save token and user info (with safe storage)
        storage.setItem('token', response.data.token);
        storage.setItem('user', JSON.stringify(response.data.user));

        // Save credentials if Remember Me is checked
        if (rememberMe) {
          storage.setItem('rememberedUsername', username);
          storage.setItem('rememberedPassword', password);
        } else {
          storage.removeItem('rememberedUsername');
          storage.removeItem('rememberedPassword');
        }

        // Call parent success callback if provided
        if (onLoginSuccess) {
          onLoginSuccess(response.data.user);
        }

        // Force page reload to trigger app re-render with authenticated state
        window.location.href = redirectUrl;
      }
    } catch (err) {
      // Extract error message - handle both string and object/array responses
      let errorMessage = t('connectionError');
      
      // Check if it's a network/timeout error (server cold start)
      if (!err.response || err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message?.includes('Network')) {
        errorMessage = t('serverStartingPleaseWait');
      } else {
        const detail = err.response?.data?.detail;
        
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          // Handle Pydantic validation error arrays
          errorMessage = detail[0].msg || detail[0].message || t('connectionError');
        } else if (detail && typeof detail === 'object') {
          // Handle Pydantic validation error objects
          errorMessage = detail.msg || detail.message || t('connectionError');
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/verify-2fa`, {
        username,
        otp
      });

      // Save token and user info (with safe storage)
      storage.setItem('token', response.data.token);
      storage.setItem('user', JSON.stringify(response.data.user));

      // Call parent success callback if provided
      if (onLoginSuccess) {
        onLoginSuccess(response.data.user);
      }

      // Force page reload to trigger app re-render with authenticated state
      window.location.href = redirectUrl;
    } catch (err) {
      // Extract error message - handle both string and object/array responses
      let errorMessage = t('invalid2FACode');
      const detail = err.response?.data?.detail;
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Handle Pydantic validation error arrays
        errorMessage = detail[0].msg || detail[0].message || t('verificationError');
      } else if (detail && typeof detail === 'object') {
        // Handle Pydantic validation error objects
        errorMessage = detail.msg || detail.message || t('verificationError');
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (step === '2fa') {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #2D0C4B 0%, #4A1D7A 50%, #6B3FA0 100%)' }}>
        <div className="relative z-10 bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-2xl w-full max-w-md border border-purple-300/30">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-purple-600 mb-2">🔐 {t('twoFactorAuth')}</h2>
            <p className="text-gray-600">{t('enterOTP')}</p>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleVerify2FA}>
            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                {t('twoFactorAuth')}
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-center text-2xl tracking-widest bg-white text-gray-900"
                placeholder="000000"
                maxLength="6"
                required
                autoFocus
              />
            </div>

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? `${t('verify')}...` : t('verify')}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep('login');
                setOtp('');
                setError('');
              }}
              className="w-full mt-3 text-purple-600 hover:text-purple-800 text-sm"
            >
              ← {t('back')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #2D0C4B 0%, #4A1D7A 50%, #6B3FA0 100%)' }}>
      {/* Decorative wave lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <svg className="absolute w-full h-full opacity-10" viewBox="0 0 1440 800" preserveAspectRatio="none">
          <path d="M0,400 C360,300 720,500 1440,350" stroke="#C783FF" strokeWidth="2" fill="none" />
          <path d="M0,500 C400,400 800,600 1440,450" stroke="#B36FF5" strokeWidth="1.5" fill="none" />
          <path d="M0,300 C300,200 900,400 1440,250" stroke="#8A4FDB" strokeWidth="1" fill="none" />
        </svg>
        <div className="absolute top-1/4 left-1/6 w-3 h-3 rounded-full bg-purple-300 opacity-30" />
        <div className="absolute top-2/3 right-1/4 w-2 h-2 rounded-full bg-pink-300 opacity-20" />
        <div className="absolute bottom-1/3 left-1/3 w-4 h-4 rounded-full bg-amber-400 opacity-25" />
      </div>

      <div className="relative z-10 bg-white/95 backdrop-blur-sm p-8 rounded-2xl shadow-2xl w-full max-w-md border border-purple-300/30" data-testid="login-card">
        <div className="text-center mb-6">
          <div className="mb-4">
            <img 
              src="/assets/banner_main.png" 
              alt="Radio Solutions" 
              className="w-full h-auto max-h-20 object-contain mx-auto"
            />
          </div>
          <h1 className="text-4xl font-bold text-purple-700 mb-2" data-testid="login-title">Radio Content</h1>
          <p className="text-gray-500">Generator</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" data-testid="login-error">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div className="mb-4">
            <label className="block text-gray-600 text-sm font-bold mb-2">
              {t('username')}
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-gray-800 text-white placeholder-gray-400"
              placeholder={t('username')}
              required
              data-testid="login-username-input"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-600 text-sm font-bold mb-2">
              {t('password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-gray-800 text-white placeholder-gray-400"
              placeholder={t('password')}
              required
              data-testid="login-password-input"
            />
          </div>

          <div className="mb-6 flex items-center">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-500 cursor-pointer">
              {t('rememberMe')}
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed text-white"
            style={{ background: 'linear-gradient(135deg, #8A4FDB 0%, #6B3FA0 100%)' }}
            data-testid="login-submit-button"
          >
            {loading ? `${t('login')}...` : t('login')}
          </button>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-400">{t('or')}</span>
          </div>
        </div>

        {/* Google Login Button */}
        {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
        <button
          type="button"
          onClick={() => {
            const redirectUrl = window.location.origin + '/auth/callback';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 hover:border-gray-300 transition"
          data-testid="login-google-button"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t('continueWithGoogle')}
        </button>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>{t('twoFactorAuth')}</p>
          
          <div className="mt-4">
            <button
              onClick={() => navigate('/trial-signup')}
              className="w-full font-bold py-3 px-4 rounded-lg transition-all duration-200 shadow-lg text-white"
              style={{ background: 'linear-gradient(135deg, #FF9F0A 0%, #FFB84D 100%)' }}
              data-testid="login-trial-button"
            >
              {t('startFreeTrial')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
